import { useEffect, useMemo, useRef, useState } from "react";

import type { ChartTableDevice } from "../../lib/chartTables.js";

function sortDevices(devices: readonly ChartTableDevice[]): ChartTableDevice[] {
  return [...devices].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
  );
}

export function chartTableLabel(device: ChartTableDevice): string {
  const name = device.name.trim() || "—";
  const venue = device.venueName?.trim();
  return venue ? `${name} · ${venue}` : name;
}

/**
 * Same table include dropdown as Health Uptime by day. Selection is owned by the caller
 * (shared `tables` URL param). Hidden when the dashboard Device filter already locks one table.
 */
export function ChartTablePicker({
  devices,
  selectedIds,
  onSelectedIdsChange,
  locked = false,
  hideInteractive = false,
}: {
  devices: readonly ChartTableDevice[];
  selectedIds: ReadonlySet<string>;
  onSelectedIdsChange: (next: Set<string>) => void;
  locked?: boolean;
  hideInteractive?: boolean;
}) {
  const sortedDevices = useMemo(() => sortDevices(devices), [devices]);
  const allIds = useMemo(() => sortedDevices.map((d) => d.id), [sortedDevices]);
  const showPicker = !hideInteractive && !locked && sortedDevices.length > 1;
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (sortedDevices.length === 0) return null;

  const selectedCount = locked ? 1 : selectedIds.size;
  const allSelected = !locked && selectedCount === allIds.length && allIds.length > 0;
  const summary = allSelected
    ? `${allIds.length} table${allIds.length === 1 ? "" : "s"}`
    : selectedCount === 0
      ? "No tables"
      : `${selectedCount} of ${allIds.length}`;

  if (!showPicker) {
    if (hideInteractive && allIds.length > 1) {
      return (
        <span className="text-[12px] font-medium text-text-secondary">Tables: {summary}</span>
      );
    }
    const only = locked
      ? (sortedDevices.find((d) => selectedIds.has(d.id)) ?? sortedDevices[0])
      : sortedDevices[0];
    return only ? (
      <span className="text-[12px] font-medium text-text-secondary">{chartTableLabel(only)}</span>
    ) : null;
  }

  const toggleId = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedIdsChange(next);
  };

  return (
    <div className="relative" ref={pickerRef}>
      <button
        type="button"
        className="rounded-md border border-border-subtle bg-panel-2 px-2 py-1 text-[12px] font-medium text-text-primary hover:bg-[#223455]"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        Tables: {summary}
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-30 mt-1 max-h-[min(50vh,280px)] w-[min(100vw-24px,260px)] overflow-auto rounded-lg border border-border bg-panel-2 p-2 shadow-xl"
          role="listbox"
          aria-multiselectable
        >
          <div className="mb-1.5 flex gap-2 border-b border-border-subtle pb-1.5">
            <button
              type="button"
              className="text-[11px] text-accent-2 hover:underline"
              onClick={() => onSelectedIdsChange(new Set(allIds))}
            >
              All
            </button>
            <button
              type="button"
              className="text-[11px] text-accent-2 hover:underline"
              onClick={() => onSelectedIdsChange(new Set())}
            >
              None
            </button>
          </div>
          <ul className="flex flex-col gap-0.5">
            {sortedDevices.map((device) => (
              <li key={device.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-[12px] text-text-primary hover:bg-panel-3">
                  <input
                    type="checkbox"
                    className="accent-accent-2"
                    checked={selectedIds.has(device.id)}
                    onChange={() => toggleId(device.id)}
                  />
                  <span className="min-w-0 truncate" title={chartTableLabel(device)}>
                    {chartTableLabel(device)}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
